import React, { useMemo } from 'react';
import { createAPIClient } from '../../api';
import { cellClass, DateField, EmptyRow, FilterGrid, ReportCard, ReportPage, ReportStatus, ReportTable, SummaryTiles, useReportData, useReportParams } from './ReportParts';
import { dateParam, enumLabel, reportDate, reportMoney } from './reportUi';

const COLUMNS = [
  { label: 'Account' }, { label: 'Name' }, { label: 'Type' },
  { label: 'Debit', numeric: true }, { label: 'Credit', numeric: true }, { label: 'Balance', numeric: true },
];

export function TrialBalanceReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { params, change } = useReportParams();
  const asOfDate = dateParam(params.get('asOfDate'));
  const state = useReportData(
    `asOf=${asOfDate}`,
    () => api.getTrialBalance({ asOfDate: asOfDate || undefined }),
    'Could not load the trial balance',
  );

  return (
    <ReportPage
      title="Trial balance"
      intro="Debit and credit totals per GL account from posted journal entries. It comes from the general ledger, so it can differ from the operational reports (AR aging, revenue, statements), for example while invoices are still DRAFT or were marked paid without a payment."
    >
      <FilterGrid>
        <DateField id="tb-as-of" label="As of" value={asOfDate} onChange={(value) => change({ asOfDate: value })} hint="Blank means today." />
      </FilterGrid>
      <ReportStatus state={state} label="trial balance">
        {(report) => (
          <>
            <SummaryTiles tiles={[
              { label: 'As of', value: reportDate(report.asOfDate) },
              { label: 'Total debits', value: reportMoney(report.totalDebits) },
              { label: 'Total credits', value: reportMoney(report.totalCredits) },
              { label: 'Status', value: report.balanced ? 'Balanced' : 'Out of balance', tone: report.balanced ? 'good' : 'bad' },
            ]} />
            <ReportCard title="Accounts">
              <ReportTable
                columns={COLUMNS}
                caption="Trial balance by account"
                footer={report.accounts.length > 0 && (
                  <tr>
                    <td className={cellClass()} colSpan={3} data-label="Account" data-primary>Totals</td>
                    <td className={cellClass(true)} data-label="Debit">{reportMoney(report.totalDebits)}</td>
                    <td className={cellClass(true)} data-label="Credit">{reportMoney(report.totalCredits)}</td>
                    <td className={cellClass(true)} data-label="Balance">{report.balanced ? 'Balanced' : 'Out of balance'}</td>
                  </tr>
                )}
              >
                {report.accounts.length === 0 ? <EmptyRow colSpan={COLUMNS.length} message="No GL activity on or before this date." /> : report.accounts.map((account) => (
                  <tr key={account.glAccountId}>
                    <td className={cellClass()} data-label="Account">{account.accountNumber}</td>
                    <td className={`${cellClass()} max-md:whitespace-normal`} data-label="Name" data-primary>{account.accountName}</td>
                    <td className={cellClass()} data-label="Type">{enumLabel(account.accountType)}</td>
                    <td className={cellClass(true)} data-label="Debit">{reportMoney(account.debit)}</td>
                    <td className={cellClass(true)} data-label="Credit">{reportMoney(account.credit)}</td>
                    <td className={cellClass(true)} data-label="Balance">{reportMoney(account.balance)}</td>
                  </tr>
                ))}
              </ReportTable>
              <p className="mt-3 text-xs text-gray-500">Balance is signed on each account's normal side: debit minus credit for debit-normal accounts, credit minus debit for credit-normal accounts. A negative balance is on the opposite side.</p>
            </ReportCard>
          </>
        )}
      </ReportStatus>
    </ReportPage>
  );
}
